// Modules
import cloneDeep from "lodash.clonedeep";
import { FormattedMessage } from "react-intl";
import { add, addDays, isAfter, setHours, setMinutes } from "date-fns";
// Material UI
import { Tooltip } from "@mui/material";
// Helpers
import {
    EVENT_SESSION,
    FREQ_NONE,
    CANCEL_ANYWAY,
    EVENT_VACATION,
    EVENT_FREE_SLOT,
    SESSION_CANCEL_ANYWAY,
    EVENT_PAID_CANCELED,
    ROLE_USER,
    EVENT_SICK_LEAVE,
    EVENT_PUBLIC_HOLIDAY,
    EVENT_COMPANY_HOLIDAY,
    THERAPIST_ABSENCE_EVENT_TYPES,
    EVENT_EDUCATIONAL_LEAVE
} from "../../../../../helpers/constants";
import { getEventDates } from "./rrule";
import { formatDate, formatTime } from "../../../../../helpers/dates";
// Assets
import EventBusyIcon from "@mui/icons-material/EventBusy";
import PublicIcon from "@mui/icons-material/Public";
import SickIcon from "@mui/icons-material/Sick";
import BusinessIcon from "@mui/icons-material/Business";
import SchoolIcon from "@mui/icons-material/School";
import Group from "@mui/icons-material/Group";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import { ID, RECURRENCE } from "../../../../../helpers/constants_db";
import { GREY } from "@/helpers/colors";
import type { EventSessionUser, UserEvent, UserEventWithTherapist } from "@/lib/types/types";
import type { PrepareEventsForCalendarType, TherapistList } from "@/lib/types/therapist/calendar";

interface EventInfo {
    event: UserEvent;
    timeText?: string;
}

// Utility function to remove unwanted fields
const removeProps = (obj, props) => props.forEach(prop => Reflect.deleteProperty(obj, prop));

/**
 *
 * @param {Object} params the event selected and the user events
 * @returns {Object} with the original event and the selected event data
 */
export function extractEventData({ event, events }) {
    const { start: startDate, end: endDate, extendedProps } = event || {};
    const originalEvent = events?.find(e => e.id === event.id);

    if ((!originalEvent?.user && originalEvent?.user_id) || (originalEvent?.user && originalEvent?.user_id)) {
        originalEvent.user = {
            _id: originalEvent?.user_id,
            name: originalEvent?.user_name,
            email: originalEvent?.user_email
        };

        removeProps(originalEvent, ["user_id", "user_name", "user_email"]);
    }

    let newEndDate = new Date(startDate);
    newEndDate.setHours(newEndDate.getHours() + 1);

    let eventData = cloneDeep(originalEvent);
    eventData.start = startDate;
    if (extendedProps?.type === EVENT_VACATION) {
        eventData.end = endDate;
    } else {
        eventData.end = newEndDate;
    }

    return { originalEvent, eventData };
}

/**
 * Maps a list of guests data to a list of guests emails and names.
 * If the input is just an email, it extracts the name from the email
 * (taking the first part before the '@' and capitalizing the first letter).
 * If the name is already provided, it returns the original object.
 *
 * @param {Array} guests List of guests data (either objects with name & email or email strings)
 * @returns {Array} A list of guests with names and emails
 */
export const mapGuestsWithNames = guests =>
    guests?.map(guest => {
        if (typeof guest === "string") {
            // If the guest is a string (email), split it and create an object with name and email
            const email = guest;
            const namePart = email.split("@")[0].split(".")[0];
            const name = namePart.charAt(0).toUpperCase() + namePart.slice(1);
            return { name, email };
        }
        // If the guest is already an object with name and email, return it as is
        return { name: guest.name, email: guest.email };
    });

/**
 * Adds an exception date to the existing ones.
 *
 * @param {Object} data the data where to add the exception date
 * @param {Date} exceptionDate the date to add as exception
 */
export function addExceptionDate(data, exceptionDate) {
    let exceptions = data.exdate ? [...data.exdate] : [];
    exceptions.push(exceptionDate.toISOString());
    data.exdate = exceptions;
}

/**
 * Adds  exceptions date to the existing ones.
 *
 * @param {Object} data the data where to add the booked dates
 * @param {Date | Date[]} exceptionDates the date or array of dates to add as booked
 */

export function addExceptionDates(data, exceptionDates) {
    let exceptions = data.exdate ? [...data.exdate] : [];

    if (!Array.isArray(exceptionDates)) {
        exceptionDates = [exceptionDates];
    }

    exceptionDates.forEach(date => {
        const isoDate = new Date(date).toISOString();
        if (!exceptions.includes(isoDate)) {
            exceptions.push(isoDate);
        }
    });

    data.exdate = exceptions;
}

/**
 * Adds  exceptions date to the existing ones.
 *
 * @param {Object} data the data where to add the booked dates
 * @param {Date | Date[]} bookedDates the date or array of dates to add as booked
 */

export function addBookedDates(data, bookedDates) {
    let booked = data.bookedDates ? [...data.bookedDates] : [];

    if (!Array.isArray(bookedDates)) {
        bookedDates = [bookedDates];
    }

    bookedDates.forEach(date => {
        const isoDate = new Date(date).toISOString();
        if (!booked.includes(isoDate)) {
            booked.push(isoDate);
        }
    });

    data.bookedDates = booked;
}

/**
 * Adds a deleted date to the existing ones.
 *
 * @param {Object} data - The data where to add the deleted date.
 * @param {Date|Date[]} deletedDate - The date or array of dates to add as deleted.
 */
export function addDeletedDates(data, deletedDates) {
    let deleted = data.deletedDate ? [...data.deletedDate] : [];

    if (!Array.isArray(deletedDates)) {
        deletedDates = [deletedDates];
    }

    deletedDates.forEach(date => {
        const isoDate = new Date(date).toISOString();
        if (!deleted.includes(isoDate)) {
            deleted.push(isoDate);
        }
    });

    data.deletedDate = deleted;
}

/**
 * Adds an canceled date to the existing ones.
 *
 * @param {Object} data the data where to add the canceled date
 * @param {Date} canceledDate the date to add as canceled
 */
export function addCanceledDate(data, canceledDate) {
    let canceled = data.canceled_dates ? [...data.canceled_dates] : [];
    canceled.push(canceledDate.toISOString());
    data.canceled_dates = canceled;
}

/**
 * Performs a delete/update session event in order to delete an event from calendar.
 * Deletes if it is a single event and updates with an exception date in case of a recurrent event.
 *
 * @param {Object} params the event and original event data, additional params and the actions of delete and update
 */
export function deleteSessionAction({
    event,
    handleSessionCreate,
    handleSessionDelete,
    handleSessionUpdate,
    messageKey,
    originalEvent,
    params
}) {
    const { id, recurrence } = event || {};

    if (recurrence === FREQ_NONE) {
        // In case of single event delete it
        handleSessionDelete({
            event,
            messageKey,
            params
        });
    } else {
        // In case of recurrent event create exception date in original event
        const { operation } = params || {};
        const { user_id, user } = originalEvent || {};

        const data = cloneDeep(originalEvent);
        data.guests = mapGuestsWithNames(data.guests);

        // Delete uncceedary data from original event
        ["user_id", "user_name", "user_email"].forEach(prop => Reflect.deleteProperty(data, prop));

        addExceptionDate(data, event.start);

        // If a user cancels a date that is to late, it will be added to the "canceled_dates" array.
        if (operation === CANCEL_ANYWAY || operation === SESSION_CANCEL_ANYWAY) addCanceledDate(data, event.start);

        // This constant will be used to store logs when deleting recurrening events.
        const eventUpdateLog = {
            date: event?.start,
            setRecurring: false
        };

        handleSessionUpdate({
            userId: user_id || user?._id,
            eventId: id,
            update: { data, eventUpdateLog },
            messageKey,
            params,
            successCb: () => {
                // Handle session creation for cancellation case
                if (operation === SESSION_CANCEL_ANYWAY) {
                    handleSessionCreate({
                        data: {
                            type: EVENT_PAID_CANCELED,
                            title: `[Abgesagt] ${event.title}`,
                            color: GREY,
                            start: event.start,
                            end: event.end,
                            guests: event.guests,
                            allDay: event.allDay
                        },
                        messageKey,
                        userId: user_id || user?._id
                    });
                }
            }
        });
    }
}

/**
 * Updates the events with some dates mapping.
 *
 * @param {Object} params the calendar ref and events to map.
 * @return {Array} the events mapped
 */
export function prepareEventsForCalendar({
    calendarRef,
    events
}: {
    calendarRef: React.RefObject<any>;
    events: UserEventWithTherapist[];
}): PrepareEventsForCalendarType[] | UserEventWithTherapist[] {
    const calendarEl = calendarRef.current;

    if (!calendarEl || !events || events.length === 0) {
        return events;
    }

    const api = calendarEl.getApi();

    return events.map((event, index) => {
        const { rrule, exdate, bookedDates, type, end } = event || {};

        // Clone the event
        let outputEvent = cloneDeep(event);

        const formatIsoWithoutTimezone = date => api.formatIso(date).split("+")[0];
        const formattedExdate = exdate?.map(formatIsoWithoutTimezone);
        const formattedBookedDates = bookedDates?.map(formatIsoWithoutTimezone);

        /**
         * ! allDay event
         * ? For all-day events, the choosen end date is considered exclusive, indicating that it will not be
         * ? included in the calculation. Therefore, a workaround is required.
         **/
        if (THERAPIST_ABSENCE_EVENT_TYPES.includes(type)) {
            const endDate = new Date(end);
            outputEvent.end = add(endDate, { days: 1 }).toISOString();
        }

        const rruleDtstartFormatted = rrule?.dtstart && formatIsoWithoutTimezone(rrule.dtstart);
        const rruleUntilFormatted = rrule?.until && formatIsoWithoutTimezone(rrule.until);

        const preparedEvent: PrepareEventsForCalendarType = {
            ...outputEvent,
            index,
            exdate: formattedExdate,
            bookedDates: formattedBookedDates,
            rrule: rrule
                ? {
                      ...event.rrule,
                      dtstart: rruleDtstartFormatted,
                      until: rruleUntilFormatted
                  }
                : undefined
        };

        return preparedEvent;
    });
}

/**
 * Render the content of a calendar event.
 *
 * @param {Object} eventInfo - An object containing event details.
 * @return {JSX.Element} - The JSX element representing the rendered event content.
 */
export function eventContent(eventInfo: EventInfo, userRole: string) {
    const { event, timeText } = eventInfo;
    const { extendedProps } = event || {};
    const isTypeVacation = extendedProps?.type === EVENT_VACATION;
    const isTypeSickLeave = extendedProps?.type === EVENT_SICK_LEAVE;
    const isTypePublicHoliday = extendedProps?.type === EVENT_PUBLIC_HOLIDAY;
    const isTypeCompanyHoliday = extendedProps?.type === EVENT_COMPANY_HOLIDAY;
    const isTypeEducationalLeave = extendedProps?.type === EVENT_EDUCATIONAL_LEAVE;
    const isGroupedSession = extendedProps?.isGroupedSession;
    const isNotUserRole = userRole !== ROLE_USER;

    const guestsCount = extendedProps?.guests?.length || 0;

    return (
        <div key={event?.id} className="fc-event-main-frame" data-testid={event?.id}>
            {!isTypeVacation && <div className="fc-event-time">{timeText}</div>}
            <div className="fc-event-title-container">
                <div className="fc-event-title fc-sticky">{event?.title}</div>
            </div>
            {isTypeVacation && isNotUserRole && <EventBusyIcon fontSize="small" />}
            {isTypeSickLeave && isNotUserRole && <SickIcon fontSize="small" />}
            {isTypePublicHoliday && isNotUserRole && <PublicIcon fontSize="small" />}
            {isTypeCompanyHoliday && isNotUserRole && <BusinessIcon fontSize="small" />}
            {isTypeEducationalLeave && isNotUserRole && <SchoolIcon fontSize="small" />}
            {isGroupedSession &&
                isNotUserRole &&
                (guestsCount === 1 ? <GroupOutlinedIcon fontSize="small" /> : <Group fontSize="small" />)}
        </div>
    );
}

/**
 * Render the content of a calendar event.
 *
 * @param {Object} eventInfo - An object containing event details.
 * @return {JSX.Element} - The JSX element representing the rendered event content.
 */
export function eventContentPatientView(eventInfo: EventInfo) {
    const { event } = eventInfo;
    const { extendedProps, start } = event || {};
    const isGroupedSession = extendedProps?.isGroupedSession;

    // Calculate if it's 5 PM one day before the event's start date
    const eventStartDate = new Date(start);
    const fivePMOneDayBefore = setMinutes(setHours(addDays(eventStartDate, -1), 17), 0);

    // Check if the current date/time is 5 PM or later the day before the event's start date
    const isFivePMOrLaterOneDayBefore = isAfter(new Date(), fivePMOneDayBefore);

    const guestsCount = extendedProps?.guests?.length || 0;

    const renderIcon = () => {
        if (!isGroupedSession) return null;

        const isPartialSession = guestsCount === 1 || (guestsCount > 1 && !isFivePMOrLaterOneDayBefore);
        // If it's a partial session and 5 PM or later one day before, don't show the icon
        if (isPartialSession && isFivePMOrLaterOneDayBefore) return null;

        const IconComponent = isPartialSession ? GroupOutlinedIcon : Group;
        const tooltipTitle = isPartialSession ? "session-type-partial-grouped" : "session-type-grouped";

        return (
            <Tooltip placement="top" title={<FormattedMessage id={tooltipTitle} />}>
                <IconComponent fontSize="small" color="primary" />
            </Tooltip>
        );
    };

    return (
        <div key={event?.id} className="fc-event-main-frame">
            <div className="fc-event-title-container" style={{ display: "flex", justifyContent: "space-between" }}>
                <div className="fc-event-title fc-sticky">{event?.title}</div>
                <div>{renderIcon()}</div>
            </div>
        </div>
    );
}

interface Guest {
    email: string;
    name: string;
    dates: string[];
    utcDates: string[];
    therapist: EventSessionUser;
}

export interface EventsGuestsAndDatesReturnType {
    events: Array<PrepareEventsForCalendarType>;
    guests: Guest[];
}

/**
 * Processes and organizes calendar events, updating the resultArray with relevant information.
 *
 * This function takes an array of calendar events, a start date, and an end date as parameters.
 * It iterates through the events, filters them based on recurrence and date range, and organizes
 * the information into the resultArray, which includes processed calendar events and associated guest details.
 *
 * @param {Array} events - An array of calendar events to be processed.
 * @param {Date} startFrom - The start date for filtering events.
 * @param {Date} startTo - The end date for filtering events.
 * @return {Object} - An object containing organized event and guest information.
 *                    - events: An array of processed calendar events.
 *                    - guests: An array of guests with associated dates.
 */
export function findEventsGuestsAndDates(
    events: PrepareEventsForCalendarType[],
    startFrom: Date,
    startTo: Date
): EventsGuestsAndDatesReturnType {
    const results: EventsGuestsAndDatesReturnType = { events: [], guests: [] };

    const isRecurrenceNone = (recurrence?: string): boolean => recurrence === FREQ_NONE;

    const addToResults = (
        event: PrepareEventsForCalendarType,
        guests: PrepareEventsForCalendarType["guests"] | undefined,
        eventDates: string[],
        eventUtcDates: string[]
    ) => {
        results.events.push(event);

        // Create a map of existing guests for faster access
        const existingGuestsMap = new Map(results.guests.map(item => [item.email, item]));

        // Create sets to store unique dates
        const guestDatesSet = new Set<string>();
        const guestUtcDatesSet = new Set<string>();

        guests?.forEach(guest => {
            if (guest.email.length === 0) return;
            // Check if the guest is part of the current event's dates
            if (event.guests?.some(user => user.email === guest.email)) {
                const guestDates = eventDates;
                const guestUtcDates = eventUtcDates;

                // Store dates in sets to ensure uniqueness
                guestDates.forEach(date => guestDatesSet.add(date));
                guestUtcDates.forEach(utcDate => guestUtcDatesSet.add(utcDate));

                // Either add a new guest or update the existing one
                const existingGuest = existingGuestsMap.get(guest.email);
                if (!existingGuest) {
                    results.guests.push({
                        email: guest.email,
                        name: guest.name,
                        dates: Array.from(guestDatesSet),
                        utcDates: Array.from(guestUtcDatesSet),
                        therapist: event.user
                    });
                } else {
                    existingGuest.dates.push(...Array.from(guestDatesSet));
                    existingGuest.utcDates.push(...Array.from(guestUtcDatesSet));
                }
            }
        });
    };

    // Function to add exception dates for assigned sessions to free slots
    const addExceptionDatesForAssignedSessionsToFreeSlots = (events: UserEvent[], event: UserEvent) => {
        const exDates = events
            .filter(session => session.type === EVENT_SESSION && event?.session_ids?.includes(session.id))
            .flatMap(session =>
                event[RECURRENCE] === FREQ_NONE
                    ? [session.start]
                    : getEventDates({ event: session, startFrom, startTo })
            );

        if (exDates.length > 0) {
            addExceptionDates(event, exDates);
        }
    };

    // Function to attach missing free slots
    const attachMissingFreeSlots = (events: PrepareEventsForCalendarType[], event: PrepareEventsForCalendarType) => {
        events.forEach(freeSlot => {
            if (freeSlot.session_ids?.includes(event[ID])) {
                const existingFreeSlot = results.events.find(slot => slot.id === freeSlot.id);

                const filteredExDates = freeSlot.exdate?.filter(date => !freeSlot.bookedDates?.includes(date));

                const exdates = Array.from(
                    new Set([
                        ...(filteredExDates || []),
                        ...(event?.recurrence === FREQ_NONE ? [event?.start] : []),
                        ...(event?.exdate || [])
                    ])
                );

                if (!existingFreeSlot) {
                    freeSlot.exdate = exdates.length > 0 ? exdates : [event?.start];
                    delete freeSlot.bookedDates;
                    results.events.push(freeSlot);
                } else {
                    const dates = exdates.length > 0 ? exdates : [event?.start];
                    existingFreeSlot.exdate = Array.from(new Set([...existingFreeSlot.exdate, ...dates]));
                }
            }
        });
    };

    events?.forEach(event => {
        const { recurrence, start, guests, type } = event;

        const eventDate = new Date(start);

        if (isRecurrenceNone(recurrence)) {
            if (!(eventDate >= startFrom && eventDate <= startTo)) return;

            if (type === EVENT_SESSION) {
                attachMissingFreeSlots(events, event);
                delete event.bookedDates;
            }

            addToResults(
                event,
                guests,
                [eventDate.toISOString()],
                [
                    `${formatDate(eventDate, { day: "2-digit", month: "2-digit" }, "de-DE")} um ${formatTime(
                        eventDate,
                        { minute: undefined },
                        "de-DE"
                    )}`
                ]
            );
        } else {
            const dates = getEventDates({ event, startFrom, startTo });

            if (dates?.length > 0) {
                let eventUpdate = cloneDeep(event);
                eventUpdate.guests = mapGuestsWithNames(eventUpdate.guests);
                addExceptionDates(eventUpdate, dates);

                if (event?.type === EVENT_FREE_SLOT) {
                    addExceptionDatesForAssignedSessionsToFreeSlots(events, eventUpdate);
                    if (event?.bookedDates && event.bookedDates.length > 0) {
                        const filteredExDates = eventUpdate?.exdate?.filter(
                            date => !event?.bookedDates?.includes(date)
                        );

                        const sessions = events
                            .filter(
                                session => session.type === EVENT_SESSION && event?.session_ids?.includes(session.id)
                            )
                            .flatMap(session => (session[RECURRENCE] === FREQ_NONE ? [session.start] : []));

                        const updatedExDates = Array.from(new Set([...(filteredExDates || []), ...(sessions || [])]));
                        eventUpdate.exdate = updatedExDates;
                        delete eventUpdate.bookedDates;
                    }
                }

                if (event?.type === EVENT_SESSION) {
                    attachMissingFreeSlots(events, eventUpdate);
                    delete eventUpdate.bookedDates;
                }

                addToResults(
                    eventUpdate,
                    eventUpdate.guests,
                    dates.map(date => new Date(date).toISOString()),
                    dates.map(
                        date =>
                            `${formatDate(date, { day: "2-digit", month: "2-digit" }, "de-DE")} um ${formatTime(
                                date,
                                { minute: undefined },
                                "de-DE"
                            )}`
                    )
                );
            }
        }
    });

    results.events = results?.events.map(event => {
        const { rrule, exdate } = event || {};

        const updateEventRRuleExdate = {
            ...event,
            ...(exdate && { exdate: exdate?.map(date => new Date(date).toISOString()) }),
            ...(rrule && {
                rrule: {
                    ...event.rrule,
                    dtstart: rrule?.dtstart && new Date(rrule?.dtstart).toISOString(),
                    until: rrule?.until && new Date(rrule?.until).toISOString()
                }
            })
        };

        return updateEventRRuleExdate;
    });

    return results;
}

export const buildBatchTherapistSelection = (therapistsList: TherapistList[]) => {
    return therapistsList.reduce(
        (acc, therapist) => ({
            ...acc,
            [therapist._id]: true
        }),
        {}
    );
};
